import {
  ArchiveIcon,
  CheckCircleIcon,
  CheckSquareIcon,
  HomeIcon,
  PencilIcon,
  PlayIcon,
  PuzzleIcon,
  RotateCcwIcon,
  ShoppingCartIcon,
  SquareIcon,
  StoreIcon,
  Trash2Icon,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { toast } from 'sonner'
import useSWR from 'swr'

import {
  PreferenceButton,
  PreferenceCategory,
  PreferenceDiv,
  PreferenceInfo,
  PreferenceInput,
  PreferenceLink,
  PreferenceSelect,
  PreferenceSubmit,
  PreferenceSwitch,
} from '../components/Preference'
import {
  isCogoBasketEnabled,
  setCogoBasketEnabled,
  subscribeCogoBasketEnabled,
} from '../lib/cogoBasketPrefs'
import {
  cogoCheckAuth,
  cogoEnsureTab,
  cogoFocusTab,
  cogoScrapeCart,
  cogoShowLoginNotice,
  probeCogoExtensionFast,
  type CogoAuth,
  type CogoExtensionStatus,
} from '../lib/cogoExtension'
import {
  canRunCogoChromeExtension,
  getCogoExtensionPrefs,
} from '../lib/cogoExtensionPrefs'
import {
  runCogoSyncWorker,
  startCogoSync,
  type CogoSyncSnapshot,
} from '../lib/cogoSyncWorker'
import { beginCogoAutomation, endCogoAutomation } from '../lib/cogoTabLifecycle'
import { useCogoBasketStatus } from '../lib/useCogoBasketStatus'
import { useCogoSyncEvents } from '../lib/useCogoSyncEvents'
import { useCogoEnrichStatus } from '../lib/useCogoEnrichStatus'
import { api } from '../lib/password'
import { fetcher } from '../lib/swr'
import { formatUnit } from '../lib/unit'
import { rankCandidateOptions } from '../lib/cogoPrefs'
import { searchMoreCogoArticles } from '../lib/cogoEnrichWorker'
import { formatCogoChoicePrice } from '../lib/cogoPrice'
import {
  choicePackUnitNotice,
  choiceUnitTypeButtons,
  choiceUnitTypes,
  unitTypeLabel,
  unitTypePrimaryBtnClass,
  unitTypeSecondaryBtnClass,
  type UnitType,
} from '../lib/cogoUnits'
import type { Shop } from '../lib/shop'
import type { CartItem, FullOrder, Product } from './types'
import CogoOrderBasket, {
  CogoBasketSyncSummary,
  type CogoBasketCandidate,
  type CogoPlanLineView,
} from './CogoOrderBasket'

type ProductQuantity = { product: Product; quantity: number }

type ChoiceOption = {
  technicalProductId: string
  display: string
  assortmentUrl?: string | null
  unitLabel?: string | null
  weightConversionFactor?: number | null
  packSize?: number | null
  unitTypes?: UnitType[] | null
  favorite?: boolean
  isBio?: boolean
  priceCents?: number | null
  hasImage?: boolean
}

function formatChoicePrice(opt: ChoiceOption): string | null {
  return formatCogoChoicePrice(opt)
}

function choiceHasPdp(opt: { assortmentUrl?: string | null }) {
  const url = opt.assortmentUrl?.trim() || ''
  return Boolean(
    url && /\/assortiment\//i.test(url) && !/rootCategoryId=/i.test(url)
  )
}

/** A product waiting for a pick, built from cogo_candidates + cogo_product_map. */
type ChoiceRow = {
  productId: number
  productDisplay: string
  neededQuantity: number
  neededUnit: string
  options: ChoiceOption[]
}

type CogoMapArticleInfo = {
  technicalProductId: string
  priority?: number
  unitType?: UnitType | null
  display?: string
  unitLabel?: string | null
  weightConversionFactor?: number | null
}

type CogoMapInfo = {
  productId?: number
  articles?: CogoMapArticleInfo[]
  /** Legacy single-row fields (ignored if articles present). */
  technicalProductId?: string | null
  display?: string
}

function mapHasArticles(map: CogoMapInfo | undefined): boolean {
  return (map?.articles?.length || 0) > 0 || Boolean(map?.technicalProductId)
}

function mapChipLabel(map: CogoMapInfo): string {
  const arts = map.articles || []
  if (arts.length === 0) {
    return map.display?.trim() || 'gemapt'
  }
  if (arts.length === 1) {
    return arts[0]!.display?.trim() || 'gemapt'
  }
  const shorts = arts
    .map(
      (a) =>
        a.unitLabel?.trim() ||
        (a.weightConversionFactor != null && a.weightConversionFactor > 0
          ? `${a.weightConversionFactor}g`
          : null)
    )
    .filter((s): s is string => Boolean(s))
  if (
    shorts.length === arts.length &&
    shorts.length <= 3 &&
    shorts.every((s) => s.length <= 10)
  ) {
    return shorts.join(', ')
  }
  return `${arts.length} artikelen`
}

type CogoCandInfo = {
  status: string
  articles?: ChoiceOption[]
}

type OrderDetailData = FullOrder & {
  cart: CartItem[]
  productQuantities: ProductQuantity[]
  shop?: Shop | null
  defaultShop?: Shop
  resolvedShop?: Shop
  cogoPlan?: CogoPlanLineView[]
}

function cartQty(cart: CartItem[], productId: number) {
  return cart.find((c) => c.productId === productId)?.quantity ?? 0
}

export default function OrderDetail({
  orderId,
  order: orderProp,
  onChange,
  onClose,
}: {
  orderId: number
  order: FullOrder
  onChange: () => void
  onClose: () => void
}) {
  orderId = orderId || orderProp?.id
  const order = useSWR<OrderDetailData>('/api/orders/' + orderId, fetcher)

  let cart: CartItem[] = order.data?.cart ?? []
  const [items, setItems] = useState<CartItem[]>()
  const [renaming, setRenaming] = useState(false)
  const [draftName, setDraftName] = useState('')
  const temp = items || cart

  useEffect(() => {
    setItems(undefined)
    setRenaming(false)
    setDraftName('')
  }, [orderId])

  const all = order.data?.productQuantities || []
  const neededByProduct = new Map(all.map((pq) => [pq.product.id, pq]))
  const trackable = all.filter((pq) => !pq.product.isInStorage)
  const storage = all.filter((pq) => pq.product.isInStorage)
  const resolvedShop: Shop = order.data?.resolvedShop || 'manual'
  const cogoPlan = order.data?.cogoPlan || []
  const isCogo = resolvedShop === 'cogo'

  // 1. Still needed (incl. partial remaining) — add to cart
  const suggestions = trackable.filter(
    (pq) => cartQty(temp, pq.product.id) < pq.quantity
  )
  // 2. Actually in this order's cart (excl. storage reminders)
  const inCart = temp
    .filter((c) => c.quantity > 0 && !c.product?.isInStorage)
    .map((c) => {
      const pq = neededByProduct.get(c.productId)
      return {
        product: c.product ?? pq?.product!,
        needed: pq?.quantity ?? c.quantity,
        ordered: c.quantity,
        messageAgent: c.messageAgent || '',
        status: c.status || 'ok',
        messageHuman: c.messageHuman || '',
        productId: c.productId,
      }
    })
    .filter((row) => row.product)

  const flush = () => {
    if (!document.startViewTransition) return setItems(undefined)
    document.startViewTransition(() => {
      flushSync(() => {
        setItems(undefined)
      })
    })
  }

  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const saveCart = async (next: CartItem[], before: CartItem[]) => {
    order.mutate((c) => ({ ...c!, cart: next }), { revalidate: false })
    if (!items) setItems(before)
    await api.patch('/api/orders/' + orderId, { cart: next })
    clearTimeout(debounce.current)
    debounce.current = setTimeout(flush, 1200)
  }

  const addToCart = async (id: number, needed: number, product: Product) => {
    const before = cart
    cart = cart.map((c) => ({ ...c }))
    const idx = cart.findIndex((c) => c.productId === id)
    if (idx >= 0) cart[idx] = { ...cart[idx]!, quantity: needed }
    else cart.push({ productId: id, quantity: needed, product })
    await saveCart(cart, before)
  }

  const removeFromCart = async (id: number) => {
    const before = cart
    cart = cart.filter((c) => c.productId !== id)
    await saveCart(cart, before)
  }

  const setProductStorage = async (
    productId: number,
    isInStorage: boolean
  ) => {
    try {
      await api.patch('/api/products/' + productId, { isInStorage })
      if (isInStorage && cartQty(cart, productId) > 0) {
        const before = cart
        cart = cart.filter((c) => c.productId !== productId)
        await saveCart(cart, before)
      }
      await order.mutate()
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : 'Kon voorraad niet bijwerken'
      )
    }
  }

  const patch = async (data: Record<string, unknown>) => {
    try {
      await api.patch('/api/orders/' + orderId, data)
      await order.mutate()
      onChange()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Bijwerken mislukt')
      throw e
    }
  }

  const [ext, setExt] = useState<CogoExtensionStatus | null>(null)
  const [cogoAuth, setCogoAuth] = useState<CogoAuth | null>(null)
  const [needsLogin, setNeedsLogin] = useState(false)
  const [showExtHelp, setShowExtHelp] = useState(false)
  const [basketOn, setBasketOn] = useState(() => isCogoBasketEnabled())
  const [launchingSync, setLaunchingSync] = useState(false)
  /** Mapped products explicitly re-opened for review (“Ander artikel”). */
  const [reviewProductIds, setReviewProductIds] = useState<Set<number>>(
    () => new Set()
  )
  const [mapsByProduct, setMapsByProduct] = useState<
    Record<string, CogoMapInfo>
  >({})
  const [candsByProduct, setCandsByProduct] = useState<
    Record<string, CogoCandInfo>
  >({})
  const [loadingMoreProductId, setLoadingMoreProductId] = useState<
    number | null
  >(null)
  /** Product id in multi-select (combine packs) mode. */
  const [combineProductId, setCombineProductId] = useState<number | null>(null)
  const [combineSelected, setCombineSelected] = useState<Set<string>>(
    () => new Set()
  )
  const workerIdRef = useRef(crypto.randomUUID())
  const abortRef = useRef<AbortController | null>(null)
  const cartRevRef = useRef(0)
  const syncLogRef = useRef<HTMLDivElement | null>(null)
  const syncJob = useCogoSyncEvents(orderId, true)
  const { label: basketLabel } = useCogoBasketStatus()
  const { active: enrichActive, status: enrichStatus } = useCogoEnrichStatus()

  useEffect(() => subscribeCogoBasketEnabled(setBasketOn), [])

  useEffect(() => {
    if (syncJob?.status === 'done' || syncJob?.status === 'waiting_choice') {
      void order.mutate()
    }
  }, [syncJob?.status])

  // Keep the visible log pinned to the latest lines of the current sync.
  useEffect(() => {
    const el = syncLogRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [syncJob?.log?.length, syncJob?.updatedAt])

  async function runSyncWorkerIfNeeded() {
    abortRef.current?.abort()
    const ac = new AbortController()
    abortRef.current = ac
    beginCogoAutomation(`sync:${orderId}`)
    try {
      await runCogoSyncWorker({
        orderId,
        workerId: workerIdRef.current,
        signal: ac.signal,
      })
    } catch (e) {
      if (!ac.signal.aborted) {
        toast.error(e instanceof Error ? e.message : 'Sync-worker gestopt')
      }
    } finally {
      endCogoAutomation(`sync:${orderId}`)
    }
  }

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [orderId])

  async function loadMapsAndCandidates(productIds: number[]) {
    if (productIds.length === 0) return
    const [mapsRes, candsRes] = await Promise.all([
      fetch('/api/cogo/maps', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productIds }),
      }),
      fetch('/api/cogo/candidates', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productIds }),
      }),
    ])
    if (mapsRes.ok) {
      const data = await mapsRes.json()
      setMapsByProduct(data.maps || {})
    }
    if (candsRes.ok) {
      const data = await candsRes.json()
      const raw = (data.candidates || {}) as Record<string, CogoCandInfo>
      const ranked: Record<string, CogoCandInfo> = {}
      for (const [k, v] of Object.entries(raw)) {
        ranked[k] = {
          ...v,
          articles: rankCandidateOptions(v.articles || []),
        }
      }
      setCandsByProduct(ranked)
    }
  }

  useEffect(() => {
    setReviewProductIds(new Set())
    setCombineProductId(null)
    setCombineSelected(new Set())
  }, [orderId])

  const suggestionIdsKey = suggestions.map((p) => p.product.id).join(',')
  const cogoPlanIdsKey = cogoPlan.map((l) => l.productId).join(',')
  useEffect(() => {
    const ids = isCogo
      ? cogoPlan.map((l) => l.productId)
      : suggestions.map((pq) => pq.product.id)
    if (ids.length === 0) return
    void loadMapsAndCandidates(ids)
    const ms =
      isCogo &&
      (enrichActive ||
        enrichStatus.phase === 'enriching' ||
        (enrichStatus.queueLength != null && enrichStatus.queueLength > 0))
        ? 3_000
        : 12_000
    const t = setInterval(() => {
      void loadMapsAndCandidates(ids)
    }, ms)
    return () => clearInterval(t)
  }, [
    orderId,
    isCogo,
    cogoPlanIdsKey,
    suggestionIdsKey,
    enrichActive,
    enrichStatus.phase,
    enrichStatus.queueLength,
  ])

  const doneToastRef = useRef<string | null>(null)
  useEffect(() => {
    if (!syncJob) return
    if (
      (syncJob.status === 'done' || syncJob.status === 'waiting_choice') &&
      syncJob.updatedAt !== doneToastRef.current
    ) {
      doneToastRef.current = syncJob.updatedAt
      const summary =
        [...syncJob.log]
          .reverse()
          .find((l) => /gesynchroniseerd/i.test(l.message)) ||
        syncJob.log[syncJob.log.length - 1]
      const failed = syncJob.items.filter(
        (i) => i.status === 'error' || i.status === 'not_found'
      )
      const choices = syncJob.items.filter((i) => i.status === 'needs_choice')
      if (failed.length > 0 || choices.length > 0) {
        toast.message(summary?.message || 'Sync klaar', {
          description: [
            failed.length
              ? `Mislukt: ${failed.map((i) => i.display).join(', ')}`
              : null,
            choices.length
              ? `Keuze nodig: ${choices.map((i) => i.display).join(', ')}`
              : null,
          ]
            .filter(Boolean)
            .join(' · '),
        })
      } else {
        toast.success(summary?.message || 'Sync klaar')
      }
      void order.mutate()
    }
    if (syncJob.status === 'error' && syncJob.lastError) {
      if (doneToastRef.current !== `err:${syncJob.updatedAt}`) {
        doneToastRef.current = `err:${syncJob.updatedAt}`
        toast.error(syncJob.lastError)
      }
    }
  }, [syncJob?.status, syncJob?.updatedAt])

  useEffect(() => {
    if (!syncJob) return
    if (syncJob.status === 'waiting_login') setNeedsLogin(true)
    if (
      syncJob.status === 'waiting_choice' ||
      syncJob.log.some((l) => l.kind === 'choice')
    ) {
      void loadMapsAndCandidates(syncJob.items.map((i) => i.productId))
    }
    if (syncJob.cartRevision > cartRevRef.current) {
      cartRevRef.current = syncJob.cartRevision
      void order.mutate()
    }
  }, [syncJob])

  useEffect(() => {
    let cancelled = false
    async function tick() {
      const status = await probeCogoExtensionFast()
      if (cancelled) return
      setExt(status)
      if (!status.installed) {
        setCogoAuth(null)
        return
      }
      if (status.loggingIn) {
        setCogoAuth({
          loggedIn: false,
          blocked: false,
          loggingIn: true,
          status: 'logging_in',
          message: 'Xtra-login bezig',
        })
        return
      }
      if (status.cogoTabCount === 0) {
        setCogoAuth({
          loggedIn: false,
          blocked: false,
          status: 'no_tab',
          message: 'Geen Collect&Go-tab open',
        })
        return
      }
      const auth = await cogoCheckAuth()
      if (!cancelled) {
        setCogoAuth(auth)
        if (auth.loggedIn) setNeedsLogin(false)
      }
    }
    void tick()
    const id = setInterval(() => void tick(), 3000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [])

  async function mapBasketCandidate(
    line: CogoPlanLineView,
    opt: CogoBasketCandidate,
    unitType: UnitType | null
  ) {
    const url = opt.assortmentUrl?.trim() || ''
    if (!url || !/\/assortiment\//i.test(url) || /rootCategoryId=/i.test(url)) {
      toast.error('Geen productpagina voor dit artikel', {
        description:
          'Zoek opnieuw via Collect&Go zodat de link bekend is, en kies opnieuw.',
      })
      return false
    }
    return pickChoice(line.productId, {
      technicalProductId: opt.technicalProductId,
      unitType,
    })
  }

  /** Write the product map for a pick and resume a sync job waiting on it. */
  async function pickChoice(
    productId: number,
    opts: {
      technicalProductId?: string | null
      unitType?: UnitType | null
      /** Multi-select entries (same priority = combo pool). */
      entries?: {
        technicalProductId: string
        unitType?: UnitType | null
        priority?: number
      }[]
    }
  ): Promise<boolean> {
    const res = await fetch(`/api/orders/${orderId}/cogo-map/${productId}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        technicalProductId: opts.technicalProductId ?? null,
        unitType: opts.unitType ?? null,
        entries: opts.entries ?? undefined,
      }),
    })
    const data = (await res.json().catch(() => ({}))) as {
      message?: string
      job?: CogoSyncSnapshot | null
    }
    if (!res.ok) {
      toast.error(data.message || 'Keuze bewaren mislukt')
      return false
    }
    toast.success(
      opts.entries && opts.entries.length > 1
        ? `${opts.entries.length} artikelen gekozen`
        : 'Artikel gekozen'
    )
    setCombineProductId(null)
    setCombineSelected(new Set())
    setReviewProductIds((prev) => {
      if (!prev.has(productId)) return prev
      const next = new Set(prev)
      next.delete(productId)
      return next
    })
    const ids = trackable.map((pq) => pq.product.id)
    void loadMapsAndCandidates(ids)
    void order.mutate()
    if (data.job?.status === 'running') {
      void runSyncWorkerIfNeeded()
    }
    return true
  }

  function startCombineSelect(ch: ChoiceRow, preselected?: string[]) {
    setCombineProductId(ch.productId)
    if (preselected && preselected.length > 0) {
      setCombineSelected(new Set(preselected))
      return
    }
    // Default: pre-check pack-sized options for a combo pool.
    const pre = new Set(
      (ch.options || [])
        .filter(
          (o) =>
            o.weightConversionFactor != null && o.weightConversionFactor > 0
        )
        .map((o) => o.technicalProductId)
    )
    setCombineSelected(pre)
  }

  function toggleCombineTpc(technicalProductId: string) {
    setCombineSelected((prev) => {
      const next = new Set(prev)
      if (next.has(technicalProductId)) next.delete(technicalProductId)
      else next.add(technicalProductId)
      return next
    })
  }

  /** Re-open choice UI for an already-mapped product (review / change). */
  async function reviewMappedChoice(pq: ProductQuantity) {
    const productId = pq.product.id
    const mapped = mapsByProduct[String(productId)]
    let articles: ChoiceOption[] =
      candsByProduct[String(productId)]?.articles || []
    if (articles.length === 0) {
      const candsRes = await fetch('/api/cogo/candidates', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productIds: [productId] }),
      })
      if (candsRes.ok) {
        const data = await candsRes.json()
        const cand = data.candidates?.[String(productId)] as
          | CogoCandInfo
          | undefined
        if (cand?.articles?.length) articles = cand.articles
        if (cand) {
          setCandsByProduct((prev) => ({
            ...prev,
            [String(productId)]: cand,
          }))
        }
      }
    }
    // Ensure currently mapped artikelen appear even if not in candidates.
    const mappedArts = mapped?.articles || []
    const byTpc = new Map(articles.map((a) => [a.technicalProductId, a]))
    for (const m of mappedArts) {
      if (!byTpc.has(m.technicalProductId)) {
        byTpc.set(m.technicalProductId, {
          technicalProductId: m.technicalProductId,
          display: m.display || m.technicalProductId,
          unitLabel: m.unitLabel,
          weightConversionFactor: m.weightConversionFactor,
          unitTypes: m.unitType ? [m.unitType] : null,
        })
      }
    }
    const options = rankCandidateOptions([...byTpc.values()])
    if (options.length === 0) {
      toast.message('Geen kandidaten', {
        description: 'Zoek eerst artikelen of wacht op verrijking.',
      })
      return
    }
    setReviewProductIds((prev) => new Set(prev).add(productId))
    const pre = mappedArts.map((a) => a.technicalProductId).filter(Boolean)
    if (pre.length === 0 && mapped?.technicalProductId) {
      pre.push(mapped.technicalProductId)
    }
    startCombineSelect(
      {
        productId,
        productDisplay: pq.product.display,
        neededQuantity: pq.quantity,
        neededUnit: pq.product.unit || '',
        options,
      },
      pre.length > 0 ? pre : undefined
    )
  }

  async function searchEmptyMatch(pq: ProductQuantity, searchTerm: string) {
    const status = await probeCogoExtensionFast()
    setExt(status)
    if (!status.installed) {
      setShowExtHelp(true)
      toast.message('Chrome-extensie nodig', {
        description:
          'Installeer de extensie om Collect&Go-artikelen te zoeken.',
      })
      return
    }
    const q = searchTerm.trim()
    if (!q) {
      toast.error('Geef een zoekterm in')
      return
    }

    const known = candsByProduct[String(pq.product.id)]?.articles || []
    const excludeTechnicalProductIds = known
      .map((a) => a.technicalProductId)
      .filter(Boolean)
    const excludeUrls = known
      .map((a) => a.assortmentUrl)
      .filter((u): u is string => Boolean(u))

    setLoadingMoreProductId(pq.product.id)
    try {
      const articles = await searchMoreCogoArticles({
        display: q,
        excludeTechnicalProductIds,
        excludeUrls,
        limit: 20,
      })
      if (articles.length === 0) {
        toast.message(
          excludeTechnicalProductIds.length > 0
            ? 'Geen nieuwe artikelen'
            : 'Geen artikelen gevonden',
          {
            description:
              excludeTechnicalProductIds.length > 0
                ? 'Deze zoekterm leverde alleen treffers die je al hebt. Probeer een andere term.'
                : 'Probeer een andere zoekterm.',
          }
        )
        return
      }
      const res = await fetch(`/api/cogo/map/${pq.product.id}/candidates`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          articles,
          excludeTechnicalProductIds,
        }),
      })
      const data = (await res.json().catch(() => ({}))) as {
        message?: string
      }
      if (!res.ok) {
        toast.error(data.message || 'Zoeken mislukt')
        return
      }
      toast.success(
        `${articles.length} artikel${articles.length === 1 ? '' : 'en'} — kies er één`
      )
      await loadMapsAndCandidates([pq.product.id])
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      if (msg === 'needs_login') {
        setNeedsLogin(true)
        toast.message('Log in bij Collect&Go', {
          description: 'Daarna opnieuw zoeken.',
        })
      } else {
        toast.error(msg === 'blocked' ? 'Collect&Go geblokkeerd' : msg)
      }
    } finally {
      setLoadingMoreProductId(null)
    }
  }

  async function loadMoreArticles(ch: ChoiceRow) {
    const display = ch.productDisplay.trim()
    if (!display) {
      toast.error('Geen productnaam voor zoeken')
      return
    }
    const status = await probeCogoExtensionFast()
    setExt(status)
    if (!status.installed) {
      setShowExtHelp(true)
      toast.message('Chrome-extensie nodig', {
        description: 'Installeer de extensie om meer artikelen te zoeken.',
      })
      return
    }
    const excludeTechnicalProductIds = (ch.options || [])
      .map((o) => o.technicalProductId)
      .filter(Boolean)
    const excludeUrls = (ch.options || [])
      .map((o) => o.assortmentUrl)
      .filter((u): u is string => Boolean(u))

    setLoadingMoreProductId(ch.productId)
    try {
      const articles = await searchMoreCogoArticles({
        display,
        excludeTechnicalProductIds,
        excludeUrls,
        limit: 20,
      })
      if (articles.length === 0) {
        toast.message('Geen nieuwe artikelen', {
          description: 'Probeer een andere zoekterm op Collect&Go.',
        })
        return
      }
      const res = await fetch(`/api/cogo/map/${ch.productId}/candidates`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          articles,
          excludeTechnicalProductIds,
        }),
      })
      const data = (await res.json().catch(() => ({}))) as {
        message?: string
        options?: ChoiceOption[]
      }
      if (!res.ok) {
        toast.error(data.message || 'Meer artikelen mislukt')
        return
      }
      toast.success(`${articles.length} nieuwe artikelen`)
      await loadMapsAndCandidates([ch.productId])
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      if (msg === 'needs_login') {
        setNeedsLogin(true)
        toast.message('Log in bij Collect&Go', {
          description: 'Daarna opnieuw Meer artikelen.',
        })
      } else {
        toast.error(msg === 'blocked' ? 'Collect&Go geblokkeerd' : msg)
      }
    } finally {
      setLoadingMoreProductId(null)
    }
  }

  async function focusCogoLogin() {
    setNeedsLogin(true)
    const authBefore = await cogoCheckAuth()
    if (authBefore.loggingIn) {
      await cogoFocusTab()
      toast.message('Xtra-login', {
        description: 'Maak het inloggen af, daarna Hervat.',
        duration: 8_000,
      })
      setCogoAuth(authBefore)
      return
    }
    await cogoEnsureTab({
      active: true,
      url: 'https://www.collectandgo.be/nl/winkelwagen',
    })
    await cogoShowLoginNotice()
    await cogoFocusTab()
    toast.message('Log in bij Collect&Go', {
      description: 'Winkelwagen-tab is geopend. Daarna Hervat.',
      duration: 8_000,
    })
    const auth = await cogoCheckAuth()
    setCogoAuth(auth)
    if (auth.loggedIn) setNeedsLogin(false)
  }

  /**
   * Manual full sync: verify Collect&Go session, scrape the cart, then set
   * every planned line to the Menu quantity (replace any in-flight job).
   */
  async function launchSync() {
    if (launchingSync) return
    setLaunchingSync(true)
    try {
      const status = await probeCogoExtensionFast()
      setExt(status)
      if (!status.installed) {
        setShowExtHelp(true)
        toast.error('Chrome-extensie niet actief')
        return
      }

      // One-shot sync only — do not enable background basket autofill.

      await cogoEnsureTab({
        active: true,
        url: 'https://www.collectandgo.be/nl/winkelwagen',
      })
      const auth = await cogoCheckAuth()
      setCogoAuth(auth)

      if (auth.blocked) {
        toast.error('Collect&Go antibot-blokkade')
        await cogoFocusTab()
        return
      }
      if (auth.loggingIn || auth.status === 'logging_in') {
        setNeedsLogin(true)
        await cogoFocusTab()
        toast.message('Xtra-login', {
          description: 'Maak het inloggen af, daarna Sync starten opnieuw.',
        })
        return
      }
      if (!auth.loggedIn) {
        setNeedsLogin(true)
        await cogoShowLoginNotice()
        await cogoFocusTab()
        toast.message('Niet ingelogd bij Collect&Go', {
          description:
            auth.message ||
            auth.status ||
            'Open de winkelwagen, log in, en start sync opnieuw.',
        })
        return
      }
      setNeedsLogin(false)

      try {
        await cogoScrapeCart()
      } catch {
        // Cart scrape is best-effort before setting absolute PDP quantities.
      }

      const items = cogoPlan
        .filter((l) => !l.skipped && l.effectiveQuantity > 0)
        .map((l) => ({
          productId: l.productId,
          quantity: l.effectiveQuantity,
          display: l.product.display,
          unit: l.product.unit || '',
        }))

      if (items.length === 0) {
        toast.message('Niets te syncen', {
          description: 'Geen producten met hoeveelheid in dit Collect&Go-plan',
        })
        return
      }

      await startCogoSync(orderId!, items, workerIdRef.current, {
        replace: true,
      })
      toast.success(`Sync gestart · ${items.length} producten`)
      void runSyncWorkerIfNeeded()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Sync starten mislukt')
    } finally {
      setLaunchingSync(false)
    }
  }

  async function setOrderShop(next: Shop) {
    await patch({ shop: next })
  }

  async function overrideCogoQty(
    productId: number,
    quantityOverride: number | null
  ) {
    const res = await fetch(`/api/orders/${orderId}/cogo-items`, {
      credentials: 'include',
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productId, quantityOverride }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(
        (err as { message?: string }).message || 'Opslaan mislukt'
      )
    }
    const data = await res.json()
    order.mutate(
      (cur) =>
        cur
          ? {
              ...cur,
              cogoPlan: Array.isArray(data.cogoPlan)
                ? data.cogoPlan
                : cur.cogoPlan,
            }
          : cur,
      { revalidate: true }
    )
  }

  async function overrideCogoBasketQuantities(
    productId: number,
    basketQuantities: Record<string, number>
  ) {
    const res = await fetch(`/api/orders/${orderId}/cogo-items`, {
      credentials: 'include',
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productId, basketQuantities }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(
        (err as { message?: string }).message || 'Opslaan mislukt'
      )
    }
    const data = await res.json()
    order.mutate(
      (cur) =>
        cur
          ? {
              ...cur,
              cogoPlan: Array.isArray(data.cogoPlan)
                ? data.cogoPlan
                : cur.cogoPlan,
            }
          : cur,
      { revalidate: true }
    )
  }

  const cogoPrefs = getCogoExtensionPrefs()
  const cogoExtCapable = canRunCogoChromeExtension()
  const extInstalled = Boolean(ext?.installed)
  /** Optimistic while probing if we have seen the extension before. */
  const extAvailable =
    extInstalled || (ext == null && cogoPrefs.everActivated && cogoExtCapable)
  const extKnownMissing = ext != null && !ext.installed
  const extStatusVersion =
    ext?.version ?? cogoPrefs.lastVersion ?? (ext ? '?' : '…')
  let cogoAuthSuffix = ''
  if (cogoAuth?.loggedIn) {
    cogoAuthSuffix = ' · ingelogd op Collect&Go'
  } else if (cogoAuth?.loggingIn) {
    cogoAuthSuffix = ' · Xtra-login bezig'
  } else if (!cogoAuth && cogoPrefs.recentlySignedIn) {
    cogoAuthSuffix = ' · ingelogd op Collect&Go'
  } else if (cogoAuth && !cogoAuth.loggedIn && cogoAuth.status !== 'no_tab') {
    cogoAuthSuffix = ' · niet ingelogd'
  }
  const showLoggingInBanner =
    extInstalled &&
    (Boolean(cogoAuth?.loggingIn) || cogoAuth?.status === 'logging_in')
  const definitiveLoggedOut =
    extInstalled &&
    cogoAuth != null &&
    !cogoAuth.loggedIn &&
    !cogoAuth.blocked &&
    !cogoAuth.loggingIn &&
    (cogoAuth.status !== 'no_tab' || !cogoPrefs.recentlySignedIn || needsLogin)
  const showLoginBanner =
    !showLoggingInBanner && (needsLogin || definitiveLoggedOut)
  const showExtInstallHelp =
    showExtHelp || (extKnownMissing && !cogoPrefs.everActivated)
  const extStatusSummary = extAvailable
    ? `v${extStatusVersion}${cogoAuthSuffix}${
        syncJob?.status === 'paused'
          ? ' · gepauzeerd'
          : syncJob?.status === 'running'
            ? ' · sync bezig'
            : syncJob?.status === 'waiting_login'
              ? ' · wacht op login'
              : ''
      }`
    : null

  /**
   * Products needing a pick: unmapped with ready candidates, flagged
   * `needs_choice` by sync, or re-opened for review. In the Collect&Go basket
   * unmapped picks are already inline, so the panel only handles remaps.
   */
  const panelChoices = (() => {
    const needsChoice = new Set(
      (syncJob?.items || [])
        .filter((i) => i.status === 'needs_choice')
        .map((i) => i.productId)
    )
    const source = isCogo
      ? cogoPlan.map((l) => ({
          product: l.product,
          quantity: l.effectiveQuantity || l.neededQuantity,
        }))
      : suggestions
    const rows: ChoiceRow[] = []
    for (const pq of source) {
      const productId = pq.product.id
      const mapped = mapHasArticles(mapsByProduct[String(productId)])
      const review = reviewProductIds.has(productId)
      const stuck = needsChoice.has(productId)
      const wanted = review || stuck || (!isCogo && !mapped)
      if (!wanted) continue
      const cand = candsByProduct[String(productId)]
      const options = cand?.articles || []
      if (options.length === 0) continue
      if (!review && !stuck && cand?.status !== 'ready') continue
      rows.push({
        productId,
        productDisplay: pq.product.display,
        neededQuantity: pq.quantity,
        neededUnit: pq.product.unit || '',
        options,
      })
    }
    return rows
  })()
  const showChoicePanel = panelChoices.length > 0

  const pendingSyncCount = cogoPlan.filter(
    (line) =>
      !line.skipped &&
      line.mapped &&
      line.effectiveQuantity > 0 &&
      line.status !== 'ok'
  ).length
  const basketSummary =
    basketOn && isCogo ? (
      <CogoBasketSyncSummary pendingCount={pendingSyncCount} />
    ) : null

  return (
    <div className="w-full min-w-0 flex flex-col gap-4">
      <div className="flex flex-wrap justify-between gap-3 pr-8 min-w-0">
        <h2 className="text-lg font-medium">
          {order.data?.display || orderProp?.display}
        </h2>
      </div>
      {showChoicePanel ? (
        <div className="rounded border border-violet-500/40 bg-violet-500/10 px-4 py-3 text-sm max-w-xl space-y-4">
          <div>
            <p className="font-medium mb-1">Kies artikel</p>
            <p className="opacity-80">
              Goedkoopste eerst — klik één artikel (vast), of combineer packs
              via vinkjes (zelfde prioriteit = combo).
            </p>
          </div>
          {panelChoices.map((ch) => {
            const packOpts = (ch.options || []).some(
              (o) =>
                o.weightConversionFactor != null && o.weightConversionFactor > 0
            )
            const weightNeed = /gram|kg|kilogram/i.test(ch.neededUnit || '')
            const showCombine = packOpts || weightNeed
            const selecting = combineProductId === ch.productId
            return (
              <div key={ch.productId} className="space-y-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-medium">
                    {ch.productDisplay || `Product ${ch.productId}`}{' '}
                    <span className="opacity-60 font-normal">
                      ({ch.neededQuantity}
                      {ch.neededUnit ? ` ${ch.neededUnit}` : ''})
                    </span>
                  </p>
                  {showCombine ? (
                    selecting ? (
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="text-xs rounded px-2.5 py-1.5 bg-white/10 hover:bg-white/20"
                          onClick={() => {
                            setCombineProductId(null)
                            setCombineSelected(new Set())
                          }}
                        >
                          Annuleer
                        </button>
                        <button
                          type="button"
                          className="text-xs rounded px-2.5 py-1.5 bg-emerald-700/80 hover:bg-emerald-600 text-white disabled:opacity-40"
                          disabled={combineSelected.size === 0}
                          onClick={() =>
                            void pickChoice(ch.productId, {
                              entries: [...combineSelected].map(
                                (technicalProductId) => ({
                                  technicalProductId,
                                  priority: 1,
                                })
                              ),
                            })
                          }
                        >
                          Bevestig
                          {combineSelected.size > 0
                            ? ` (${combineSelected.size})`
                            : ''}
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="text-xs rounded px-2.5 py-1.5 bg-emerald-700/80 hover:bg-emerald-600 text-white"
                        onClick={() => startCombineSelect(ch)}
                      >
                        Combineer packs
                      </button>
                    )
                  ) : null}
                </div>
                {selecting ? (
                  <p className="text-xs opacity-70">
                    Vink acceptabele packs aan — sync kiest de goedkoopste combo
                    binnen die set (prioriteit 1).
                  </p>
                ) : null}
                <ul className="space-y-2">
                  {(Array.isArray(ch.options) ? ch.options : []).map((opt) => {
                    const modes = choiceUnitTypes(opt)
                    const multi = modes.length > 1
                    const buttons = choiceUnitTypeButtons(modes)
                    const packNotice = choicePackUnitNotice(modes)
                    const price = formatChoicePrice(opt)
                    const checked = combineSelected.has(opt.technicalProductId)
                    const meta = [
                      opt.unitLabel,
                      opt.weightConversionFactor
                        ? `${opt.weightConversionFactor}g`
                        : null,
                      opt.packSize && opt.packSize > 1
                        ? `${opt.packSize}st`
                        : null,
                      opt.isBio ? 'BIO' : null,
                      opt.favorite ? 'favoriet' : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                    const body = (
                      <>
                        {opt.hasImage !== false ? (
                          <img
                            src={`/api/cogo/articles/${encodeURIComponent(opt.technicalProductId)}/image`}
                            alt=""
                            className="w-12 h-12 object-contain rounded bg-white/5 shrink-0"
                            loading="lazy"
                            onError={(e) => {
                              ;(e.target as HTMLImageElement).style.display =
                                'none'
                            }}
                          />
                        ) : null}
                        <span className="min-w-0 flex-1">
                          <span className="font-medium block">
                            {opt.display}
                          </span>
                          <span className="opacity-60 text-xs block mt-0.5">
                            {meta}
                          </span>
                          {!choiceHasPdp(opt) ? (
                            <span className="text-amber-200/90 text-xs block mt-0.5">
                              Geen productpagina — zoek opnieuw in Collect&Go
                            </span>
                          ) : null}
                          {!selecting && multi ? (
                            <span className="flex flex-col items-start gap-1 mt-2">
                              <span className="flex flex-wrap items-center gap-2">
                                {buttons.map((mode, i) => (
                                  <button
                                    key={mode}
                                    type="button"
                                    disabled={!choiceHasPdp(opt)}
                                    className={
                                      i === 0
                                        ? unitTypePrimaryBtnClass
                                        : unitTypeSecondaryBtnClass
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      if (!choiceHasPdp(opt)) return
                                      void pickChoice(ch.productId, {
                                        technicalProductId:
                                          opt.technicalProductId,
                                        unitType: mode,
                                      })
                                    }}
                                  >
                                    {unitTypeLabel(mode)}
                                  </button>
                                ))}
                              </span>
                              {packNotice ? (
                                <button
                                  type="button"
                                  disabled={!choiceHasPdp(opt)}
                                  className="text-xs text-sky-300 hover:underline disabled:opacity-50"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    if (!choiceHasPdp(opt)) return
                                    void pickChoice(ch.productId, {
                                      technicalProductId:
                                        opt.technicalProductId,
                                      unitType: 'PA',
                                    })
                                  }}
                                >
                                  Ook per pak
                                </button>
                              ) : null}
                            </span>
                          ) : null}
                        </span>
                        {price ? (
                          <span className="shrink-0 font-medium tabular-nums text-sm">
                            {price}
                          </span>
                        ) : null}
                      </>
                    )
                    if (selecting) {
                      return (
                        <li key={opt.technicalProductId}>
                          <label
                            className={`w-full text-left rounded px-3 py-2 flex gap-3 items-start cursor-pointer ${
                              checked
                                ? 'bg-emerald-600/25 ring-1 ring-emerald-500/50'
                                : 'bg-white/10 hover:bg-white/15'
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="mt-1.5 shrink-0"
                              checked={checked}
                              onChange={() =>
                                toggleCombineTpc(opt.technicalProductId)
                              }
                            />
                            {body}
                          </label>
                        </li>
                      )
                    }
                    return (
                      <li key={opt.technicalProductId}>
                        {multi ? (
                          <div className="w-full text-left rounded px-3 py-2 bg-white/10 flex gap-3 items-start">
                            {body}
                          </div>
                        ) : (
                          <button
                            type="button"
                            disabled={!choiceHasPdp(opt)}
                            title={
                              choiceHasPdp(opt)
                                ? undefined
                                : 'Geen productpagina — zoek opnieuw in Collect&Go'
                            }
                            className="w-full text-left rounded px-3 py-2 bg-white/10 hover:bg-white/20 flex gap-3 items-start disabled:opacity-50"
                            onClick={() => {
                              if (!choiceHasPdp(opt)) return
                              void pickChoice(ch.productId, {
                                technicalProductId: opt.technicalProductId,
                                unitType: null,
                              })
                            }}
                          >
                            {body}
                          </button>
                        )}
                      </li>
                    )
                  })}
                </ul>
                <button
                  type="button"
                  className="text-xs rounded px-2.5 py-1.5 bg-white/10 hover:bg-white/20 disabled:opacity-50"
                  disabled={loadingMoreProductId === ch.productId}
                  onClick={() => void loadMoreArticles(ch)}
                >
                  {loadingMoreProductId === ch.productId
                    ? 'Zoeken…'
                    : 'Meer artikelen'}
                </button>
              </div>
            )
          })}
        </div>
      ) : null}

      {/* Collect&Go order basket */}
      {isCogo ? (
        <section className="w-full min-w-0 flex flex-col">
          <CogoOrderBasket
            lines={cogoPlan}
            candidatesByProduct={candsByProduct}
            basketSummary={basketSummary}
            enrichSearching={
              enrichActive ||
              enrichStatus.phase === 'enriching' ||
              enrichStatus.phase === 'waiting_ext' ||
              (enrichStatus.queueLength != null &&
                enrichStatus.queueLength > 0) ||
              loadingMoreProductId != null
            }
            enrichClaimProductId={
              enrichStatus.currentProductId ?? loadingMoreProductId ?? null
            }
            onOverride={overrideCogoQty}
            onBasketQuantities={overrideCogoBasketQuantities}
            onPickCandidate={mapBasketCandidate}
            onMoveToStorage={(productId) =>
              void setProductStorage(productId, true)
            }
            onChangeArticle={(line) =>
              void reviewMappedChoice({
                product: line.product,
                quantity: line.effectiveQuantity || line.neededQuantity,
              })
            }
            onSearchEmpty={(line, term) =>
              void searchEmptyMatch(
                {
                  product: line.product,
                  quantity: line.effectiveQuantity || line.neededQuantity,
                },
                term
              )
            }
          />
        </section>
      ) : (
        <>
          <section className="flex flex-col">
            <h3 className="text-sm font-medium opacity-60 mb-1">
              Nog te bestellen
            </h3>
            {suggestions.length === 0 ? (
              <p className="text-sm opacity-40 py-1">Alles in de winkelwagen</p>
            ) : (
              suggestions.map((pq) => {
                const ordered = cartQty(cart, pq.product.id)
                const remaining = Math.max(pq.quantity - ordered, 0)
                const justAdded = ordered >= pq.quantity
                return (
                  <ShopItem
                    key={'suggest-' + pq.product.id}
                    product={pq.product}
                    quantity={remaining || pq.quantity}
                    ordered={ordered}
                    needed={pq.quantity}
                    onClick={() =>
                      addToCart(pq.product.id, pq.quantity, pq.product)
                    }
                    action="add"
                    highlight={justAdded}
                    transitionName={'suggest-' + pq.product.id}
                    onMoveToStorage={() =>
                      void setProductStorage(pq.product.id, true)
                    }
                  />
                )
              })
            )}
          </section>

          <section className="flex flex-col mt-4">
            <h3 className="text-sm font-medium opacity-60 mb-1">Winkelwagen</h3>
            {inCart.length === 0 ? (
              <p className="text-sm opacity-40 py-1">Nog niets toegevoegd</p>
            ) : (
              inCart.map((row) => {
                const ordered = cartQty(cart, row.product.id)
                const justRemoved = ordered === 0
                return (
                  <ShopItem
                    key={'cart-' + row.product.id}
                    product={row.product}
                    quantity={ordered || row.ordered}
                    ordered={ordered}
                    needed={row.needed}
                    onClick={() => removeFromCart(row.product.id)}
                    action="remove"
                    highlight={justRemoved}
                    transitionName={'cart-' + row.product.id}
                  />
                )
              })
            )}
          </section>
        </>
      )}

      {storage.length > 0 ? (
        <section className="flex flex-col mt-4">
          <h3 className="text-sm font-medium opacity-60 mb-1">Voorraad</h3>
          {storage.map((pq) => (
            <div
              key={'storage-' + pq.product.id}
              className="flex items-center gap-4 py-1"
            >
              <button
                type="button"
                className="shrink-0 focus:outline-hidden"
                title="Verplaats naar boodschappen"
                aria-label={`${pq.product.display} uit voorraad halen`}
                aria-checked={true}
                role="checkbox"
                onClick={() => void setProductStorage(pq.product.id, false)}
              >
                <CheckSquareIcon className="text-lime-600" />
              </button>
              <div className="min-w-[50px] text-right opacity-70">
                {pq.quantity} {formatUnit(pq.product.unit)}
              </div>
              <div className="font-semibold truncate opacity-70">
                {pq.product.display}
              </div>
            </div>
          ))}
        </section>
      ) : null}

      <div className="mt-4 -mb-8 px-4 -mx-4 sm:-mx-6 bg-green-950 text-white">
        {isCogo && cogoPlan.length > 0 ? (
          <PreferenceCategory filled title="Collect&Go">
            {extAvailable ? (
              <>
                <PreferenceSwitch
                  icon={<ShoppingCartIcon className="h-6 w-6" />}
                  title="Winkelwagen in achtergrond"
                  summary={
                    basketOn
                      ? basketLabel
                      : 'Producten klaarzetten om te bestellen'
                  }
                  value={basketOn}
                  onValueChange={(v) => {
                    setCogoBasketEnabled(v)
                    setBasketOn(v)
                  }}
                />
                <PreferenceButton
                  icon={<PlayIcon className="h-6 w-6" />}
                  title={launchingSync ? 'Sync starten…' : 'Sync starten'}
                  summary="Controleert login en zet alle hoeveelheden goed in Collect&Go"
                  disabled={launchingSync}
                  onPress={() => void launchSync()}
                />
                {basketOn && basketSummary ? (
                  <PreferenceDiv className="px-5 py-3 text-sm opacity-70">
                    {basketSummary}
                  </PreferenceDiv>
                ) : null}
                {basketOn && extStatusSummary ? (
                  <PreferenceInfo
                    icon={<PuzzleIcon className="h-6 w-6" />}
                    title="Extensie"
                    summary={extStatusSummary}
                  />
                ) : null}
                {basketOn && showLoggingInBanner ? (
                  <PreferenceButton
                    title="Xtra-login bezig"
                    summary="Maak het inloggen af — sync gaat daarna verder"
                    onPress={() => void focusCogoLogin()}
                  />
                ) : null}
                {basketOn && showLoginBanner ? (
                  <PreferenceButton
                    title={
                      cogoAuth?.status === 'no_tab'
                        ? 'Collect&Go nog niet open'
                        : 'Niet ingelogd bij Collect&Go'
                    }
                    summary={
                      cogoAuth?.message ||
                      'Open de winkelwagen en log in om autofill te hervatten'
                    }
                    onPress={() => void focusCogoLogin()}
                  />
                ) : null}
                {basketOn && cogoAuth?.blocked ? (
                  <PreferenceButton
                    title="Antibot-blokkade"
                    summary="Los de captcha op in de Collect&Go-tab"
                    onPress={() => void cogoFocusTab()}
                  />
                ) : null}
                {syncJob && syncJob.log.length > 0 ? (
                  <PreferenceDiv className="px-5 py-3">
                    <div className="text-xs opacity-50 mb-1">
                      Sync-log
                      {syncJob.status === 'paused'
                        ? ' (gepauzeerd)'
                        : syncJob.status === 'running'
                          ? ' (bezig…)'
                          : syncJob.status === 'waiting_choice'
                            ? ' (wacht op keuze)'
                            : syncJob.status === 'done'
                              ? ' (laatste sync)'
                              : ''}
                    </div>
                    <div
                      ref={syncLogRef}
                      className="text-sm font-mono opacity-80 max-h-56 overflow-y-auto space-y-0.5"
                    >
                      {syncJob.log.map((line) => (
                        <div
                          key={line.id}
                          className={
                            line.kind === 'error'
                              ? 'text-red-300'
                              : line.kind === 'done'
                                ? 'text-lime-300'
                                : line.kind === 'login' ||
                                    line.kind === 'choice'
                                  ? 'text-amber-200'
                                  : ''
                          }
                        >
                          <span className="opacity-50">
                            {new Date(line.at).toLocaleTimeString('nl-BE', {
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                            })}
                          </span>{' '}
                          {line.message}
                        </div>
                      ))}
                    </div>
                  </PreferenceDiv>
                ) : null}
                <PreferenceLink
                  icon={<PuzzleIcon className="h-6 w-6" />}
                  title="Collect&Go-instellingen"
                  summary={
                    showExtInstallHelp
                      ? 'Chrome-extensie installeren en configureren'
                      : 'Extensie, voorkeuren'
                  }
                  href="/settings/cogo"
                />
              </>
            ) : !cogoExtCapable ? (
              <PreferenceLink
                icon={<PuzzleIcon className="h-6 w-6" />}
                title="Alleen in Chrome op desktop"
                summary="Open deze bestelling in Chrome op een computer voor Collect&Go-autofill"
                href="/settings/cogo/products"
              />
            ) : cogoPrefs.everActivated ? (
              <>
                <PreferenceInfo
                  icon={<PuzzleIcon className="h-6 w-6" />}
                  title="Integratie niet actief"
                  summary={
                    cogoPrefs.lastVersion
                      ? `Chrome-extensie niet bereikbaar (laatst v${cogoPrefs.lastVersion})`
                      : 'Chrome-extensie is momenteel niet bereikbaar'
                  }
                />
                <PreferenceLink
                  icon={<PuzzleIcon className="h-6 w-6" />}
                  title="Collect&Go-instellingen"
                  summary="Extensie controleren of herinstalleren"
                  href="/settings/cogo"
                />
              </>
            ) : (
              <PreferenceLink
                icon={<PuzzleIcon className="h-6 w-6" />}
                title="Collect&Go-instellingen"
                summary="Chrome-extensie installeren en configureren"
                href="/settings/cogo"
              />
            )}
          </PreferenceCategory>
        ) : null}

        <PreferenceCategory
          filled
          title={order.data?.deliveredAt ? 'Afgerond' : 'Bestelling'}
        >
          {!order.data ? null : order.data.deliveredAt ? (
            <>
              <PreferenceButton
                icon={<ArchiveIcon className="h-6 w-6" />}
                title="Archiveer"
                summary="Verplaats naar archief"
                onPress={() => {
                  void patch({ archivedAt: new Date().toJSON() }).then(() =>
                    onClose()
                  )
                }}
              />
              <PreferenceButton
                icon={<RotateCcwIcon className="h-6 w-6" />}
                title="Terug"
                summary="Nog niet afgerond"
                onPress={() => void patch({ deliveredAt: null })}
              />
            </>
          ) : (
            <>
              {renaming ? (
                <>
                  <PreferenceInput
                    title="Naam"
                    value={draftName}
                    onChange={setDraftName}
                    autoFocus
                    placeholder="Naam van de bestelling"
                  />
                  <PreferenceSubmit
                    title="Bewaar"
                    onPress={() => {
                      const display = draftName.trim()
                      if (!display) {
                        toast.error('Naam is verplicht')
                        return
                      }
                      void patch({ display }).then(() => setRenaming(false))
                    }}
                  />
                  <PreferenceButton
                    title="Annuleer"
                    onPress={() => setRenaming(false)}
                  />
                </>
              ) : (
                <>
                  <PreferenceButton
                    icon={<PencilIcon className="h-6 w-6" />}
                    title="Hernoem"
                    summary={order.data.display || 'Naamloos'}
                    onPress={() => {
                      setDraftName(order.data?.display || '')
                      setRenaming(true)
                    }}
                  />
                  <PreferenceButton
                    icon={<HomeIcon className="h-6 w-6" />}
                    title="Markeer als besteld"
                    summary='Gerechten komen "In huis"'
                    onPress={() => {
                      const at = new Date().toJSON()
                      void patch({ orderedAt: at, deliveredAt: at })
                    }}
                  />
                  {!isCogo ? (
                    <PreferenceButton
                      icon={<RotateCcwIcon className="h-6 w-6" />}
                      title="Reset winkelwagen"
                      summary="Uncheck alle ingrediënten uit winkelwagen"
                      isDestructive
                      onPress={() => void patch({ cart: [] })}
                    />
                  ) : null}
                  {order.data.orderedAt ? (
                    <PreferenceButton
                      icon={<RotateCcwIcon className="h-6 w-6" />}
                      title="Terug"
                      summary="Nog niet afgerond"
                      onPress={() => void patch({ orderedAt: null })}
                    />
                  ) : null}
                  <PreferenceButton
                    icon={<Trash2Icon className="h-6 w-6" />}
                    title="Verwijderen"
                    summary="Bestelling en openstaande gerechten wissen"
                    isDestructive
                    onPress={() => {
                      if (
                        !confirm(
                          'Deze bestelling definitief verwijderen? Gerechten die nog niet "In huis" staan gaan mee.'
                        )
                      ) {
                        return
                      }
                      void api
                        .delete('/api/orders/' + orderId)
                        .then(() => {
                          onChange()
                          onClose()
                        })
                        .catch((e) => {
                          toast.error(
                            e instanceof Error
                              ? e.message
                              : 'Verwijderen mislukt'
                          )
                        })
                    }}
                  />
                </>
              )}
            </>
          )}
        </PreferenceCategory>

        <PreferenceCategory filled title="Bestelmodus">
          <PreferenceSelect
            icon={<StoreIcon className="h-6 w-6" />}
            title="Modus voor deze bestelling"
            value={isCogo ? 'cogo' : 'manual'}
            options={[
              {
                value: 'manual',
                label: 'Handmatig',
                summary: 'Checklist in Menu',
              },
              {
                value: 'cogo',
                label: 'Collect&Go',
                summary: 'Artikelen via Collect&Go',
              },
            ]}
            onValueChange={(v) =>
              void setOrderShop(v === 'cogo' ? 'cogo' : 'manual')
            }
          />
        </PreferenceCategory>
      </div>
    </div>
  )
}

function ShopItem({
  product,
  onClick,
  quantity,
  ordered,
  needed,
  highlight,
  action,
  transitionName,
  messageAgent,
  status,
  messageHuman,
  onEditHumanNote,
  cogoChip,
  onCogoChipClick,
  onMoveToStorage,
}: {
  product: Product
  onClick: () => void
  quantity: number
  ordered: number
  needed: number
  highlight: boolean
  action: 'add' | 'remove'
  transitionName: string
  messageAgent?: string
  status?: CartItem['status']
  messageHuman?: string
  onEditHumanNote?: () => void
  cogoChip?: string
  onCogoChipClick?: () => void
  onMoveToStorage?: () => void
}) {
  const partial = ordered > 0 && ordered < needed
  const checked = action === 'remove' ? ordered > 0 : ordered >= needed
  const statusTone =
    status === 'not_found'
      ? 'text-amber-500'
      : status === 'to_remove'
        ? 'text-red-500'
        : ''
  return (
    <div
      style={{ viewTransitionName: transitionName }}
      className={
        (highlight
          ? action === 'add'
            ? 'text-lime-600'
            : 'text-red-600'
          : '') + ' shopitem flex flex-col py-1'
      }
    >
      <div className="flex items-center gap-2 w-full">
        {onMoveToStorage ? (
          <button
            type="button"
            className="shrink-0 focus:outline-hidden"
            title="Verplaats naar voorraad"
            aria-label={`${product.display} naar voorraad`}
            aria-checked={false}
            role="checkbox"
            onClick={onMoveToStorage}
          >
            <SquareIcon
              className={
                (partial ? 'text-lime-600 opacity-70' : 'opacity-30') +
                ' shrink-0'
              }
            />
          </button>
        ) : null}
        <button
          className="flex items-center gap-4 hover:underline focus-visible:underline focus:outline-hidden text-left min-w-0 flex-1"
          type="button"
          onClick={onClick}
        >
          {!onMoveToStorage ? (
            checked ? (
              <CheckSquareIcon className="text-lime-600 shrink-0" />
            ) : (
              <SquareIcon
                className={
                  (partial ? 'text-lime-600 opacity-70' : 'opacity-30') +
                  ' shrink-0'
                }
              />
            )
          ) : null}
          <div className="min-w-[50px] text-right">
            {quantity} {formatUnit(product.unit)}
          </div>
          <div className="font-semibold truncate">{product.display}</div>
          {partial ? (
            <div className="text-sm opacity-60 shrink-0">
              ({ordered}/{needed})
            </div>
          ) : null}
        </button>
        {cogoChip ? (
          onCogoChipClick ? (
            <button
              type="button"
              className="text-xs opacity-70 hover:opacity-100 underline shrink-0 max-w-[40%] truncate text-right"
              title={`${cogoChip} — klik om te wijzigen`}
              onClick={onCogoChipClick}
            >
              {cogoChip}
            </button>
          ) : (
            <span
              className="text-xs opacity-50 shrink-0 max-w-[40%] truncate text-right"
              title={cogoChip}
            >
              {cogoChip}
            </span>
          )
        ) : null}
      </div>
      {action === 'remove' &&
      (messageAgent || messageHuman || (status && status !== 'ok')) ? (
        <div className="ml-10 pl-1 flex flex-col gap-0.5 text-sm">
          {status && status !== 'ok' ? (
            <span className={statusTone}>
              {status === 'not_found'
                ? 'Niet gevonden'
                : status === 'to_remove'
                  ? 'Te verwijderen'
                  : status}
            </span>
          ) : null}
          {messageAgent ? (
            <span className="opacity-70">{messageAgent}</span>
          ) : null}
          {onEditHumanNote ? (
            <button
              type="button"
              className="text-left opacity-50 hover:opacity-90 hover:underline w-fit"
              onClick={onEditHumanNote}
            >
              {messageHuman || 'Notitie toevoegen…'}
            </button>
          ) : null}
        </div>
      ) : action === 'remove' && onEditHumanNote ? (
        <button
          type="button"
          className="ml-10 pl-1 text-sm text-left opacity-40 hover:opacity-80 hover:underline w-fit"
          onClick={onEditHumanNote}
        >
          Notitie toevoegen…
        </button>
      ) : null}
    </div>
  )
}
